import React from "react";
import { Home, Heart, Phone, Euro, Users, Building2, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { CountyHomeCare } from "@/hooks/use-county-home-care";
import { HomeCareProviderCard } from "@/components/home-care/HomeCareProviderCard";
import { useHomeCareProviders } from "@/hooks/use-home-care-providers";

interface HomeCareSectionProps {
  countyName: string;
  countySlug?: string;
  homeCare: CountyHomeCare;
}

export function HomeCareSection({ countyName, countySlug, homeCare }: HomeCareSectionProps) {
  const { data: providers } = useHomeCareProviders({
    search: "",
    county: countySlug || null,
    service: null,
    hseApprovedOnly: false,
    minRating: null,
  });

  // Show top 6 providers sorted by rating desc
  const topProviders = (providers || [])
    .sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0))
    .slice(0, 6);

  const sections = [
    {
      id: "hse-services",
      icon: Building2,
      title: `HSE Home Care Services in ${countyName}`,
      content: homeCare.hse_services,
    },
    {
      id: "private-providers",
      icon: Users,
      title: `Private Home Care Providers in ${countyName}`,
      content: homeCare.private_providers,
    },
    {
      id: "home-care-packages",
      icon: Heart,
      title: "Home Care Packages & How to Apply",
      content: homeCare.home_care_packages,
    },
    {
      id: "cost-info",
      icon: Euro,
      title: `Home Care Costs in ${countyName}`,
      content: homeCare.cost_info,
    },
    {
      id: "key-contacts",
      icon: Phone,
      title: "Key Contacts & Resources",
      content: homeCare.key_contacts,
    },
  ].filter(s => s.content);

  if (sections.length === 0 && topProviders.length === 0) return null;

  return (
    <section className="mt-16" id="home-care">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-primary/10">
          <Home className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            Home Care Options in {countyName}
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Alternatives to residential nursing home care
          </p>
        </div>
      </div>

      {homeCare.overview && (
        <div className="prose prose-sm max-w-none text-muted-foreground mb-8">
          {homeCare.overview.split('\n').map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      )}

      {/* Top-rated provider cards */}
      {topProviders.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Top Home Care Providers in {countyName}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {topProviders.map((provider) => (
              <HomeCareProviderCard key={provider.id} provider={provider} />
            ))}
          </div>
          {(providers?.length || 0) > 6 && (
            <div className="mt-4 text-center">
              <Button variant="outline" asChild>
                <Link to={`/home-care?county=${countySlug}`}>
                  View all {providers?.length} providers in {countyName}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          )}
        </div>
      )}

      {sections.length > 0 && (
        <Accordion type="multiple" className="space-y-3">
          {sections.map(({ id, icon: Icon, title, content }) => (
            <AccordionItem key={id} value={id} className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-primary shrink-0" />
                  <span className="text-left font-semibold">{title}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="prose prose-sm max-w-none text-muted-foreground pl-8">
                  {content!.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </section>
  );
}
